import {
  DeleteFilled,
  LinkedinFilled,
  PlusOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import {
  Company,
  CREATE_KEY_PERSON,
  DELETE_KEY_PERSON,
  ErrorResponse,
  GET_COMPANY,
  KeyPerson,
  LIST_KEY_PERSON,
  UPDATE_KEY_PERSON,
} from '@frontend/data-access';
import { IsEmptyRichText } from '@frontend/shared/ui';
import { App, Button, Form, Input, Modal, Spin } from 'antd';
import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useState } from 'react';

interface FormValues {
  description: string;
}

function EditKeyPersonForm({
  companyId,
}: {
  companyId: number;
  onFinish: () => void;
  onCancel: () => void;
}) {
  const { notification } = App.useApp();
  const { t } = useTranslation('common');
  const { data, loading } = useQuery<{ company: Company }>(GET_COMPANY, {
    variables: { id: companyId },
  });
  const [isIncomplete, setIsIncomplete] = useState(false);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [updatingKeyPerson, setUpdatingKeyPerson] = useState<KeyPerson | null>(
    null
  );
  const [form] = Form.useForm();

  const formFieldRules = {
    name: [
      { required: true, message: '' },
      () => ({
        validator(_: unknown, value: string) {
          if (value) {
            if (!IsEmptyRichText(value)) return Promise.resolve();
          }
          return Promise.reject(new Error(t('name_is_required')));
        },
      }),
    ],
    role: [
      { required: true, message: '' },
      () => ({
        validator(_: unknown, value: string) {
          if (value) {
            if (!IsEmptyRichText(value)) return Promise.resolve();
          }
          return Promise.reject(new Error('Role is required'));
        },
      }),
    ],
  };

  const { data: dataKeyPerson } = useQuery<{
    listKeyPerson: KeyPerson[];
  }>(LIST_KEY_PERSON, {
    variables: {
      companyId: {
        companyId: companyId,
      },
    },
  });

  const handleCompleted = (message: string) => {
    notification.success({
      message: message,
    });
    setUpdatingKeyPerson(null);
    setAddModalVisible(false);
    form.resetFields();
  };

  const handleError = (data: ErrorResponse) => {
    notification.error({
      message: t('error'),
      description: data.message,
    });
  };

  const refreshQueries = [
    {
      query: LIST_KEY_PERSON,
      variables: { companyId: { companyId: companyId } },
    },
    { query: GET_COMPANY, variables: { id: companyId } },
  ];

  const [create, { loading: createLoading }] = useMutation(CREATE_KEY_PERSON, {
    onCompleted: () => handleCompleted(t('successfully_created')),
    refetchQueries: refreshQueries,
    onError: handleError,
  });

  const [update, { loading: updateLoading }] = useMutation(UPDATE_KEY_PERSON, {
    onCompleted: () => handleCompleted(t('successfully_updated')),
    refetchQueries: refreshQueries,
    onError: handleError,
  });

  const [deleteKeyPerson, { loading: deleteLoading }] = useMutation(
    DELETE_KEY_PERSON,
    {
      onCompleted: () => handleCompleted(t('successfully_deleted')),
      refetchQueries: refreshQueries,
      onError: handleError,
    }
  );

  const handleOnCreate = (values: FormValues) => {
    create({
      variables: {
        input: {
          companyId: companyId,
          ...values,
        },
      },
    });
  };

  const handleOnUpdate = (values: FormValues) => {
    update({
      variables: {
        input: {
          companyId: companyId,
          keyPersonId: updatingKeyPerson?.id,
          ...values,
        },
      },
    });
  };

  const handleOnDelete = (data: KeyPerson) => {
    setUpdatingKeyPerson(data);
    deleteKeyPerson({
      variables: {
        input: {
          companyId: companyId,
          keyPersonId: data.id,
        },
      },
    });
  };

  const handleFormChange = () => {
    const hasErrors = form.getFieldsError().some(({ errors }) => errors.length);
    setIsIncomplete(hasErrors);
  };

  useEffect(() => {
    if (data?.company) {
      form.setFieldsValue({ topicIds: data?.company.topics?.map((i) => i.id) });
    }
  }, [data, form]);

  if (loading) {
    return (
      <div className="text-center p-6">
        <Spin />
      </div>
    );
  }

  return (
    <>
      {dataKeyPerson?.listKeyPerson.length ? (
        dataKeyPerson?.listKeyPerson.map((keyPerson) => (
          <div key={keyPerson.id} data-test="key-person-item">
            <div className="flex items-center">
              <div className="grow">
                <div className="font-semibold">{keyPerson.name}</div>
                <div className="font-light text-gray-700">{keyPerson.role}</div>
              </div>
              <div>
                {keyPerson.linkedinUrl && (
                  <Link href={keyPerson.linkedinUrl} target="_blank">
                    <Button shape="circle" icon={<LinkedinFilled />} />
                  </Link>
                )}
              </div>
              <div>
                <Button
                  type="link"
                  shape="round"
                  onClick={() => {
                    setUpdatingKeyPerson(keyPerson);
                    form.setFieldsValue({
                      name: keyPerson.name,
                      role: keyPerson.role,
                      linkedinUrl: keyPerson.linkedinUrl,
                    });
                    setAddModalVisible(true);
                  }}
                >
                  {t('edit')}
                </Button>
                <Button
                  shape="circle"
                  loading={updatingKeyPerson === keyPerson && deleteLoading}
                  onClick={() => {
                    handleOnDelete(keyPerson);
                  }}
                  icon={<DeleteFilled />}
                />
              </div>
            </div>
          </div>
        ))
      ) : (
        <div className="pt-6">
          <div className="text-center">
            <TeamOutlined />
            <h2 className="font-medium">{t('key_persons')}</h2>
            <div className="font-light text-gray-700 mb-10">
              {t('no_key_persons')}
            </div>
          </div>
        </div>
      )}
      <div className="flex justify-end items-center gap-2">
        <Button
          className="w-full"
          type="link"
          shape="round"
          icon={<PlusOutlined />}
          data-test="add-key-person-btn"
          onClick={() => {
            form.resetFields();
            setAddModalVisible(true);
          }}
        >
          {t('add_person')}
        </Button>
      </div>
      <Modal
        footer={false}
        title={updatingKeyPerson ? t('edit_key_person') : t('add_key_person')}
        open={addModalVisible}
        onCancel={() => setAddModalVisible(false)}
      >
        <Form
          form={form}
          layout="vertical"
          onFieldsChange={handleFormChange}
          onFinish={updatingKeyPerson ? handleOnUpdate : handleOnCreate}
        >
          <Form.Item
            name="name"
            label={t('full_name')}
            rules={formFieldRules.name}
          >
            <Input
              placeholder={t('full_name')}
              maxLength={100}
              data-test="key-person-name-input"
            />
          </Form.Item>
          <Form.Item name="role" label={t('role')} rules={formFieldRules.role}>
            <Input
              placeholder={t('role_institution')}
              maxLength={100}
              data-test="key-person-role-input"
            />
          </Form.Item>
          <Form.Item
            name="linkedinUrl"
            label={
              <div>
                LinkedIN <span className="text-gray-700">{t('optional')}</span>
              </div>
            }
          >
            <Input
              placeholder={t('linkedin_url')}
              type="link"
              maxLength={100}
              data-test="key-person-linkedin-input"
            />
          </Form.Item>
          <div className="flex justify-end items-center gap-2">
            <Button
              htmlType="submit"
              type="primary"
              loading={updatingKeyPerson ? updateLoading : createLoading}
              disabled={isIncomplete}
              shape="round"
              data-test="key-person-save-btn"
            >
              {t('save')}
            </Button>
          </div>
        </Form>
      </Modal>
    </>
  );
}

export default EditKeyPersonForm;
